import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Transition } from '@headlessui/react';
import {
  FiX, FiSave, FiSend, FiCheck, FiCornerDownLeft, FiClock,
  FiXCircle, FiRefreshCw, FiTrash2, FiChevronDown, FiChevronUp, FiActivity, FiRepeat,
  FiUser, FiBriefcase,
} from 'react-icons/fi';
import PostComposer from './PostComposer';
import PostPreview from './PostPreview';
import PostStatusBadge from './PostStatusBadge';
import PlatformBadge from './PlatformBadge';
import SchedulePicker, { formatPstDisplay, isInPast } from './SchedulePicker';
import { POST_STATUSES, EDITABLE_STATUSES, TERMINAL_STATUSES } from '../../services/SocialPostService';
import {
  updatePost, submitForApproval, approvePost, rejectPost,
  queuePost, cancelPost, retryPost, deletePost, createPost,
} from '../../store/thunks/socialPostsActions';
import { clearCurrentPost } from '../../store/slices/socialPostsSlice';

const ACCOUNT_LABELS = { personal: 'Personal', business: 'Business' };

export default function PostDetailDrawer({ post, open, onClose, onSaved }) {
  const dispatch = useDispatch();
  const { actionLoading } = useSelector((s) => s.socialPosts);
  const [content, setContent] = useState('');
  const [platform, setPlatform] = useState('linkedin');
  const [account, setAccount] = useState('business');
  const [mediaUrl, setMediaUrl] = useState(null);
  const [mediaFile, setMediaFile] = useState(null);
  const [scheduledAt, setScheduledAt] = useState(null);
  const [repostConfig, setRepostConfig] = useState({ enabled: false, target_account: 'personal', delay_minutes: 0 });
  const [showPreview, setShowPreview] = useState(true);
  const [showActivity, setShowActivity] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  const autosaveTimer = useRef(null);
  const lastSavedContent = useRef('');

  const isNew = !post?._id;
  const status = post?.status || POST_STATUSES.draft;
  const isEditable = isNew || EDITABLE_STATUSES.has(status);
  const isTerminal = TERMINAL_STATUSES.has(status);
  const isRepost = post?.post_type === 'repost';

  useEffect(() => {
    if (post) {
      setContent(post.content || '');
      setPlatform(post.platform || 'linkedin');
      setAccount(post.account || 'business');
      setMediaUrl(post.media_url || null);
      setScheduledAt(post.scheduled_at || null);
      setRepostConfig(post.repost_config || { enabled: false, target_account: post.account === 'personal' ? 'business' : 'personal', delay_minutes: 0 });
      lastSavedContent.current = post.content || '';
    } else {
      setContent('');
      setPlatform('linkedin');
      setAccount('business');
      setMediaUrl(null);
      setMediaFile(null);
      setScheduledAt(null);
      setRepostConfig({ enabled: false, target_account: 'personal', delay_minutes: 0 });
      lastSavedContent.current = '';
    }
    setErrors({});
    setToast(null);
  }, [post]);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => {
    if (!post?._id || status !== POST_STATUSES.draft) return;
    if (content === lastSavedContent.current) return;
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      try {
        await dispatch(updatePost({ postId: post._id, updateData: { content } })).unwrap();
        lastSavedContent.current = content;
      } catch { /* silent */ }
    }, 3000);
    return () => clearTimeout(autosaveTimer.current);
  }, [content, post?._id, status, dispatch]);

  const validate = useCallback(() => {
    const e = {};
    if (!content.trim()) e.content = 'Post content is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [content]);

  const handleSaveDraft = useCallback(async () => {
    if (!validate()) return;
    try {
      if (isNew) {
        await dispatch(createPost({ platform, account, content, scheduled_at: scheduledAt, repost_config: repostConfig })).unwrap();
        showToast('Draft saved');
      } else {
        await dispatch(updatePost({
          postId: post._id,
          updateData: { content, scheduled_at: scheduledAt, account, repost_config: repostConfig },
        })).unwrap();
        lastSavedContent.current = content;
        showToast('Draft updated');
      }
      onSaved?.();
    } catch (err) {
      showToast(err?.error || 'Failed to save', 'error');
    }
  }, [validate, isNew, platform, account, content, scheduledAt, repostConfig, post, dispatch, showToast, onSaved]);

  const handleSubmitForApproval = useCallback(async () => {
    if (!validate()) return;
    try {
      if (isNew) {
        const result = await dispatch(createPost({ platform, account, content, scheduled_at: scheduledAt, repost_config: repostConfig })).unwrap();
        const id = result?.post?._id || result?._id;
        if (id) await dispatch(submitForApproval(id)).unwrap();
      } else {
        if (content !== lastSavedContent.current) {
          await dispatch(updatePost({ postId: post._id, updateData: { content, scheduled_at: scheduledAt, account, repost_config: repostConfig } })).unwrap();
        }
        await dispatch(submitForApproval(post._id)).unwrap();
      }
      showToast('Submitted for approval');
      onSaved?.();
    } catch (err) {
      showToast(err?.error || 'Failed to submit', 'error');
    }
  }, [validate, isNew, platform, account, content, scheduledAt, repostConfig, post, dispatch, showToast, onSaved]);

  const handleApprove = useCallback(async () => {
    try {
      await dispatch(approvePost(post._id)).unwrap();
      showToast('Post approved');
      onSaved?.();
    } catch (err) { showToast(err?.error || 'Failed to approve', 'error'); }
  }, [post, dispatch, showToast, onSaved]);

  const handleReject = useCallback(async () => {
    try {
      await dispatch(rejectPost({ postId: post._id })).unwrap();
      showToast('Sent back to draft');
      onSaved?.();
    } catch (err) { showToast(err?.error || 'Failed to reject', 'error'); }
  }, [post, dispatch, showToast, onSaved]);

  const handleQueue = useCallback(async () => {
    if (!scheduledAt) { setErrors({ schedule: 'Schedule time is required to queue' }); return; }
    if (isInPast(scheduledAt)) { setErrors({ schedule: 'Cannot schedule in the past' }); return; }
    try {
      await dispatch(queuePost({ postId: post._id, scheduled_at: scheduledAt })).unwrap();
      showToast('Post queued for publishing');
      onSaved?.();
    } catch (err) { showToast(err?.error || 'Failed to queue', 'error'); }
  }, [scheduledAt, post, dispatch, showToast, onSaved]);

  const handleCancel = useCallback(async () => {
    try {
      await dispatch(cancelPost(post._id)).unwrap();
      showToast('Post canceled');
      onSaved?.();
    } catch (err) { showToast(err?.error || 'Failed to cancel', 'error'); }
  }, [post, dispatch, showToast, onSaved]);

  const handleRetry = useCallback(async () => {
    try {
      await dispatch(retryPost(post._id)).unwrap();
      showToast('Retry queued');
      onSaved?.();
    } catch (err) { showToast(err?.error || 'Failed to retry', 'error'); }
  }, [post, dispatch, showToast, onSaved]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm('Delete this draft permanently?')) return;
    try {
      await dispatch(deletePost(post._id)).unwrap();
      showToast('Draft deleted');
      onClose?.();
    } catch (err) { showToast(err?.error || 'Failed to delete', 'error'); }
  }, [post, dispatch, showToast, onClose]);

  const handleClose = useCallback(() => {
    dispatch(clearCurrentPost());
    onClose?.();
  }, [dispatch, onClose]);

  return (
    <>
      <Transition
        show={open}
        enter="transition-opacity duration-200" enterFrom="opacity-0" enterTo="opacity-100"
        leave="transition-opacity duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={handleClose} />
      </Transition>

      <Transition
        show={open}
        enter="transition-transform duration-300 ease-out" enterFrom="translate-x-full" enterTo="translate-x-0"
        leave="transition-transform duration-200 ease-in" leaveFrom="translate-x-0" leaveTo="translate-x-full"
      >
        <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col border-l border-zinc-200">
          {/* Header */}
          <div className="px-6 py-4 border-b border-zinc-100 bg-zinc-50/50">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-zinc-900">
                {isNew ? 'New Post' : isRepost ? 'Repost' : 'Edit Post'}
              </h2>
              <button onClick={handleClose} className="p-2 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors">
                <FiX className="w-5 h-5" />
              </button>
            </div>
            {/* Account + status bar — always visible */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${
                account === 'personal' ? 'bg-violet-100 text-violet-700' : 'bg-indigo-100 text-indigo-700'
              }`}>
                {account === 'personal' ? <FiUser className="w-3.5 h-3.5" /> : <FiBriefcase className="w-3.5 h-3.5" />}
                Posting as {ACCOUNT_LABELS[account] || account}
              </div>
              <PlatformBadge platform={platform} size="xs" />
              {!isNew && <PostStatusBadge status={status} size="sm" />}
              {isRepost && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-amber-50 text-amber-600 text-[11px] font-medium">
                  <FiRepeat className="w-3 h-3" /> Native Repost
                </span>
              )}
            </div>
          </div>

          {toast && (
            <div className={`mx-6 mt-3 px-4 py-2.5 rounded-xl text-sm font-medium ${
              toast.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
            }`}>{toast.msg}</div>
          )}

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            <PostComposer
              content={content}
              platform={platform}
              account={account}
              mediaUrl={mediaFile || mediaUrl}
              repostConfig={repostConfig}
              onContentChange={setContent}
              onPlatformChange={isNew ? setPlatform : undefined}
              onAccountChange={isEditable ? setAccount : undefined}
              onMediaChange={setMediaFile}
              onMediaRemove={() => { setMediaFile(null); setMediaUrl(null); }}
              onRepostConfigChange={isEditable && !isRepost ? setRepostConfig : undefined}
              disabled={!isEditable}
              errors={errors}
              compact
            />

            <SchedulePicker
              value={scheduledAt}
              onChange={setScheduledAt}
              disabled={isTerminal || status === POST_STATUSES.publishing}
              error={errors.schedule}
              label="Schedule (PST)"
            />

            <div>
              <button onClick={() => setShowPreview(!showPreview)} className="flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-800 transition-colors">
                {showPreview ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
                Preview
              </button>
              {showPreview && (
                <div className="mt-3">
                  <PostPreview content={content} platform={platform} account={account} mediaUrl={mediaFile || mediaUrl} />
                </div>
              )}
            </div>

            {post?.activity_log?.length > 0 && (
              <div>
                <button onClick={() => setShowActivity(!showActivity)} className="flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-800 transition-colors">
                  <FiActivity className="w-4 h-4" />
                  Activity Log ({post.activity_log.length})
                  {showActivity ? <FiChevronUp className="w-3.5 h-3.5" /> : <FiChevronDown className="w-3.5 h-3.5" />}
                </button>
                {showActivity && (
                  <div className="mt-3 space-y-2 pl-2 border-l-2 border-zinc-200">
                    {post.activity_log.map((entry, i) => (
                      <div key={i} className="pl-3 py-1.5">
                        <p className="text-xs font-medium text-zinc-700">{entry.action?.replace(/_/g, ' ')}</p>
                        <p className="text-[11px] text-zinc-400">
                          {entry.by?.firstName} {entry.by?.lastName}
                          {entry.at && ` · ${formatPstDisplay(entry.at)}`}
                        </p>
                        {entry.note && <p className="text-[11px] text-zinc-500 mt-0.5">{entry.note}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!isNew && (
              <div className="text-xs text-zinc-400 space-y-1 pt-2 border-t border-zinc-100">
                {post?.created_by && <p>Created by {post.created_by.firstName} {post.created_by.lastName}</p>}
                {post?.approved_by && <p>Approved by {post.approved_by.firstName} {post.approved_by.lastName} {post.approved_at && `· ${formatPstDisplay(post.approved_at)}`}</p>}
                {post?.published_at && <p>Published {formatPstDisplay(post.published_at)}</p>}
                {post?.canceled_at && <p>Canceled {formatPstDisplay(post.canceled_at)}</p>}
                {post?.failure_reason && <p className="text-red-500">Failure: {post.failure_reason}</p>}
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="border-t border-zinc-100 px-6 py-4 bg-zinc-50/50">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {status === POST_STATUSES.draft && !isNew && (
                  <button onClick={handleDelete} disabled={actionLoading} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50">
                    <FiTrash2 className="w-4 h-4" /> Delete
                  </button>
                )}
                {status === POST_STATUSES.queued && (
                  <button onClick={handleCancel} disabled={actionLoading} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50">
                    <FiXCircle className="w-4 h-4" /> Cancel Post
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                {(isNew || status === POST_STATUSES.draft) && (
                  <>
                    <button onClick={handleSaveDraft} disabled={actionLoading} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium border border-zinc-300 text-zinc-700 bg-white hover:bg-zinc-50 transition-colors disabled:opacity-50">
                      <FiSave className="w-4 h-4" /> Save Draft
                    </button>
                    <button onClick={handleSubmitForApproval} disabled={actionLoading} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm hover:shadow-md transition-all disabled:opacity-50">
                      <FiSend className="w-4 h-4" /> Submit for Approval
                    </button>
                  </>
                )}
                {status === POST_STATUSES.pending_approval && (
                  <>
                    <button onClick={handleReject} disabled={actionLoading} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium border border-zinc-300 text-zinc-700 bg-white hover:bg-zinc-50 transition-colors disabled:opacity-50">
                      <FiCornerDownLeft className="w-4 h-4" /> Send Back
                    </button>
                    <button onClick={handleApprove} disabled={actionLoading} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm hover:shadow-md transition-all disabled:opacity-50">
                      <FiCheck className="w-4 h-4" /> Approve
                    </button>
                  </>
                )}
                {status === POST_STATUSES.approved && (
                  <>
                    <button onClick={handleSaveDraft} disabled={actionLoading} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium border border-zinc-300 text-zinc-700 bg-white hover:bg-zinc-50 transition-colors disabled:opacity-50">
                      <FiSave className="w-4 h-4" /> Save Changes
                    </button>
                    <button onClick={handleQueue} disabled={actionLoading} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm hover:shadow-md transition-all disabled:opacity-50">
                      <FiClock className="w-4 h-4" /> Approve & Queue
                    </button>
                  </>
                )}
                {status === POST_STATUSES.queued && (
                  <button onClick={handleSaveDraft} disabled={actionLoading} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm hover:shadow-md transition-all disabled:opacity-50">
                    <FiSave className="w-4 h-4" /> Save & Reschedule
                  </button>
                )}
                {status === POST_STATUSES.failed && (
                  <>
                    <button onClick={handleSaveDraft} disabled={actionLoading} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium border border-zinc-300 text-zinc-700 bg-white hover:bg-zinc-50 transition-colors disabled:opacity-50">
                      <FiSave className="w-4 h-4" /> Save Edit
                    </button>
                    <button onClick={handleRetry} disabled={actionLoading} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 shadow-sm hover:shadow-md transition-all disabled:opacity-50">
                      <FiRefreshCw className="w-4 h-4" /> Retry
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </>
  );
}
